/*  Batch-conductor sizing · row-expanded editor.
    Renders the mode-specific NEC inputs (motor branch / transformer /
    motor group feeder) inline underneath a table row when the user
    clicks the ▸ toggle. Kept in its own file so the main
    BatchConductor.jsx stays digestible.
*/
import { MOTOR_PROTECTIONS } from "./constants";

function CellInput({ value, onChange, type = "text", className = "", testid }) {
  return (
    <input type={type} value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={`bg-black/40 border border-white/10 px-1.5 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none ${className}`}
      data-testid={testid} />
  );
}

function CellSelect({ value, onChange, options, className = "", testid }) {
  return (
    <select value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={`bg-black/40 border border-white/10 px-1 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none ${className}`}
      data-testid={testid}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Field({ label, children, compact = false }) {
  return (
    <label className={`block ${compact ? "text-[10px]" : ""}`}>
      <span className={`label-tag block mb-1 ${compact ? "text-[9px]" : ""}`}>{label}</span>
      {children}
    </label>
  );
}

function updateMotor(row, setCell, i, patch) {
  const list = (row.motor_group_motors || []).map((m, j) => (j === i ? { ...m, ...patch } : m));
  setCell({ motor_group_motors: list });
}

export default function RowExpanded({ row, setCell }) {
  return (
    <div className="space-y-4" data-testid="batch-row-expanded">
      {/* Shared advanced fields */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Power factor">
          <CellInput type="number" value={row.power_factor}
            onChange={(v) => setCell({ power_factor: Number(v) })}
            className="w-full" testid="batch-exp-pf" />
        </Field>
        <Field label="Cables in media">
          <CellInput type="number" value={row.cables_in_media}
            onChange={(v) => setCell({ cables_in_media: Number(v) })}
            className="w-full" testid="batch-exp-cim" />
        </Field>
        <Field label="Location">
          <CellSelect value={row.location_wet ? "wet" : "dry"}
            onChange={(v) => setCell({ location_wet: v === "wet" })}
            options={[
              { value: "dry", label: "Dry" },
              { value: "wet", label: "Wet" },
            ]}
            className="w-full" testid="batch-exp-loc" />
        </Field>
        <Field label="Bundling">
          <CellSelect value={row.bundled ? "yes" : "no"}
            onChange={(v) => setCell({ bundled: v === "yes" })}
            options={[
              { value: "yes", label: "Bundled" },
              { value: "no", label: "Spaced ≥ 1 OD" },
            ]}
            className="w-full" testid="batch-exp-bundled" />
        </Field>
        <Field label="Neutral in conduit">
          <CellSelect value={row.include_neutral_in_conduit ? "yes" : "no"}
            onChange={(v) => setCell({ include_neutral_in_conduit: v === "yes" })}
            options={[
              { value: "yes", label: "Include" },
              { value: "no", label: "Exclude" },
            ]}
            className="w-full" testid="batch-exp-neutral" />
        </Field>
        <Field label="Code">
          <CellSelect value={row.code}
            onChange={(v) => setCell({ code: v })}
            options={[
              { value: "NEC", label: "NEC (US)" },
              { value: "CEC", label: "CEC (Canada)" },
            ]}
            className="w-full" testid="batch-exp-code" />
        </Field>
      </div>

      {/* Motor branch */}
      {row.circuit_type === "motor_branch" && (
        <div className="border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-3">
          <div className="label-tag mb-2">// motor branch (NEC 430.52 + 430.32)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Motor protection">
              <CellSelect value={row.motor_protection}
                onChange={(v) => setCell({ motor_protection: v })}
                options={MOTOR_PROTECTIONS}
                className="w-full" testid="batch-exp-mp" />
            </Field>
            <Field label="Service factor">
              <CellSelect value={row.motor_service_factor}
                onChange={(v) => setCell({ motor_service_factor: Number(v) })}
                options={[
                  { value: 1.15, label: "1.15" },
                  { value: 1.0, label: "1.00" },
                ]}
                className="w-full" testid="batch-exp-msf" />
            </Field>
          </div>
        </div>
      )}

      {/* Transformer */}
      {row.circuit_type === "transformer" && (
        <div className="border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-3">
          <div className="label-tag mb-2">// transformer (NEC 450.3)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Rated kVA">
              <CellInput type="number" value={row.transformer_kva}
                onChange={(v) => setCell({ transformer_kva: Number(v) })}
                className="w-full" testid="batch-exp-kva" />
            </Field>
            <Field label="Secondary V">
              <CellInput type="number" value={row.transformer_secondary_voltage}
                onChange={(v) => setCell({ transformer_secondary_voltage: Number(v) })}
                className="w-full" testid="batch-exp-secv" />
            </Field>
            <Field label="This conductor is on">
              <CellSelect value={row.transformer_side}
                onChange={(v) => setCell({ transformer_side: v })}
                options={[
                  { value: "primary", label: "Primary side" },
                  { value: "secondary", label: "Secondary side" },
                ]}
                className="w-full" testid="batch-exp-side" />
            </Field>
            <Field label="Secondary OCPD present?">
              <CellSelect value={row.transformer_has_secondary_protection ? "yes" : "no"}
                onChange={(v) => setCell({ transformer_has_secondary_protection: v === "yes" })}
                options={[
                  { value: "no", label: "No" },
                  { value: "yes", label: "Yes" },
                ]}
                className="w-full" testid="batch-exp-secocpd" />
            </Field>
            {/* v2.87 · Supervised location + both-sides sizing */}
            <label className="flex items-center gap-2 text-[11px] col-span-2"
                   title="NEC 450.3(B) 'Supervised locations only' — primary-only ≥ 9 A jumps from 125 % → 250 %.">
              <input type="checkbox"
                checked={!!row.transformer_supervised}
                onChange={(e) => setCell({ transformer_supervised: e.target.checked })}
                className="accent-[#FFCC00]"
                data-testid="batch-exp-tx-supervised" />
              <span>Supervised location (450.3(B) exception)</span>
            </label>
            <label className="flex items-center gap-2 text-[11px] col-span-2"
                   title="Size PRIMARY and SECONDARY cables + OCPDs in one row using the per-side lengths below.">
              <input type="checkbox"
                checked={!!row.transformer_size_both_sides}
                onChange={(e) => setCell({ transformer_size_both_sides: e.target.checked })}
                className="accent-[#00E5FF]"
                data-testid="batch-exp-tx-both-sides" />
              <span className="font-bold">Size both sides in one shot</span>
            </label>
            {row.transformer_size_both_sides && (
              <>
                <Field label="Primary length ft" compact>
                  <CellInput type="number" value={row.transformer_primary_length_ft ?? ""}
                    onChange={(v) => setCell({ transformer_primary_length_ft: Number(v) })}
                    className="w-full" testid="batch-exp-tx-pri-len" />
                </Field>
                <Field label="Secondary length ft" compact>
                  <CellInput type="number" value={row.transformer_secondary_length_ft ?? ""}
                    onChange={(v) => setCell({ transformer_secondary_length_ft: Number(v) })}
                    className="w-full" testid="batch-exp-tx-sec-len" />
                </Field>
              </>
            )}
          </div>
        </div>
      )}

      {/* Motor group feeder */}
      {row.circuit_type === "motor_group" && (
        <div className="border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="label-tag">// motor group feeder (NEC 430.24)</div>
            <button
              onClick={() => setCell({
                motor_group_motors: [
                  ...(row.motor_group_motors || []),
                  { tag: `M-${(row.motor_group_motors?.length || 0) + 1}`, hp: 5 },
                ],
              })}
              className="text-[10px] font-mono text-[#00E5FF] border border-[#00E5FF]/40 px-2 py-0.5 hover:bg-[#00E5FF]/10"
              data-testid="batch-exp-mg-add">
              + Add motor
            </button>
          </div>
          <div className="space-y-2">
            {(row.motor_group_motors || []).map((m, i) => (
              <div key={i} className="grid grid-cols-6 gap-2 items-end" data-testid={`batch-exp-mg-${i}`}>
                <Field label="Tag" compact>
                  <CellInput value={m.tag ?? ""}
                    onChange={(v) => updateMotor(row, setCell, i, { tag: v })}
                    className="w-full" testid={`batch-exp-mg-${i}-tag`} />
                </Field>
                <Field label="hp" compact>
                  <CellInput type="number" value={m.hp ?? ""}
                    onChange={(v) => updateMotor(row, setCell, i, { hp: v === "" ? null : Number(v) })}
                    className="w-full" testid={`batch-exp-mg-${i}-hp`} />
                </Field>
                <Field label="FLA (opt)" compact>
                  <CellInput type="number" value={m.fla ?? ""}
                    onChange={(v) => updateMotor(row, setCell, i, { fla: v === "" ? null : Number(v) })}
                    className="w-full" testid={`batch-exp-mg-${i}-fla`} />
                </Field>
                <Field label="V (opt)" compact>
                  <CellInput type="number" value={m.voltage ?? ""}
                    onChange={(v) => updateMotor(row, setCell, i, { voltage: v === "" ? "" : Number(v) })}
                    className="w-full" testid={`batch-exp-mg-${i}-v`} />
                </Field>
                <Field label="φ (opt)" compact>
                  <CellSelect value={m.phases ?? ""}
                    onChange={(v) => updateMotor(row, setCell, i, { phases: v === "" ? "" : Number(v) })}
                    options={[
                      { value: "", label: "—" },
                      { value: 1, label: "1" },
                      { value: 3, label: "3" },
                    ]}
                    className="w-full" testid={`batch-exp-mg-${i}-phases`} />
                </Field>
                <div>
                  <button
                    onClick={() => {
                      const list = (row.motor_group_motors || []).filter((_, j) => j !== i);
                      setCell({ motor_group_motors: list.length ? list : row.motor_group_motors });
                    }}
                    disabled={(row.motor_group_motors || []).length <= 1}
                    className="text-gray-400 hover:text-[#FF3B30] disabled:opacity-30 text-xs font-mono"
                    data-testid={`batch-exp-mg-${i}-remove`}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs font-mono text-gray-400">
            <span>Other non-motor loads:</span>
            <CellInput type="number" value={row.motor_group_other_loads_amps}
              onChange={(v) => setCell({ motor_group_other_loads_amps: Number(v) })}
              className="w-24" testid="batch-exp-mg-other" />
            <span className="text-gray-500">A · added at 100 % per NEC 430.24</span>
          </div>
        </div>
      )}
    </div>
  );
}
